import { Check, ChevronsUpDown } from 'lucide-react';
import type { ProductCandidate, ProductOption } from '@/api/cockpit';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/utils';

export interface ProductSelectorLine {
  lineNumber: number;
  needsReview?: boolean;
  matchedProductIsObsolete?: boolean;
  matchedProductDisplay?: string | null;
  productCandidates?: readonly ProductCandidate[];
}

export interface ProductSelectorProps {
  line: ProductSelectorLine;
  isOpen: boolean;
  disabled?: boolean;
  destructive?: boolean;
  searchValue: string;
  debouncedSearchValue: string;
  searchResults: readonly ProductOption[];
  selectedProducts: Record<number, string>;
  onOpenChange: (open: boolean) => void;
  onSearchChange: (value: string) => void;
  onSelect: (itemNumber: string) => void;
}

export function ProductSelector(props: ProductSelectorProps): JSX.Element {
  const selectedDisplay = resolveSelectedProductDisplay(
    props.line,
    props.searchResults,
    props.selectedProducts,
  );

  return (
    <Popover
      open={props.disabled ? false : props.isOpen}
      onOpenChange={(open) => {
        if (props.disabled) return;
        props.onOpenChange(open);
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          role="combobox"
          disabled={props.disabled}
          className={cn(
            'w-full justify-between font-normal',
            // Soft amber tint for "wants a second look but not blocking";
            // applies only when the row isn't already in the destructive
            // (blocking) state.
            props.line.needsReview && !props.destructive && 'border-warning/60 bg-warning/5',
            // Blocking state: solid destructive surface with foreground
            // text -- matches the line-number badge and bottom indicator
            // pill, so the morph between them stays color-consistent and
            // the contrast reads cleanly in both themes.
            props.destructive &&
              'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive hover:brightness-110',
          )}
          title={selectedDisplay}
        >
          <ProductSelectorLabel
            display={selectedDisplay}
            isObsolete={props.line.matchedProductIsObsolete === true}
          />
          <ChevronsUpDown className="h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-[32rem] border bg-background p-0 text-foreground shadow-lg"
        align="start"
      >
        <Command className="bg-background text-foreground">
          <CommandInput
            placeholder="Search product by ID or name..."
            value={props.searchValue}
            onValueChange={props.onSearchChange}
          />
          <CommandList>
            <ProductSearchEmpty debouncedSearchValue={props.debouncedSearchValue} />
            <CommandGroup heading="Top candidates">
              {(props.line.productCandidates ?? []).map((candidate) => (
                <ProductCandidateItem
                  key={`candidate-${props.line.lineNumber}-${candidate.id}`}
                  candidate={candidate}
                  isSelected={
                    props.selectedProducts[props.line.lineNumber] === candidate.itemNumber
                  }
                  onSelect={() => {
                    if (props.disabled) return;
                    props.onSelect(candidate.itemNumber);
                  }}
                />
              ))}
            </CommandGroup>
            <CommandSeparator />
            <CommandGroup heading="Catalog">
              <ProductCatalogContent
                debouncedSearchValue={props.debouncedSearchValue}
                options={buildCatalogOptions(
                  props.searchResults,
                  props.line.productCandidates ?? [],
                )}
                selectedItemNumber={props.selectedProducts[props.line.lineNumber]}
                onSelect={(itemNumber) => {
                  if (props.disabled) return;
                  props.onSelect(itemNumber);
                }}
                lineNumber={props.line.lineNumber}
              />
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

function ProductSelectorLabel(props: { display: string; isObsolete: boolean }): JSX.Element {
  return (
    <span className="flex min-w-0 items-center gap-2">
      <span className="truncate">{props.display || 'Select product'}</span>
      {props.isObsolete ? (
        <Badge variant="destructive" className="shrink-0">
          Obsolete
        </Badge>
      ) : null}
    </span>
  );
}

function ProductSearchEmpty(props: { debouncedSearchValue: string }): JSX.Element | null {
  if (props.debouncedSearchValue.length < 2) return null;
  return <CommandEmpty>No product found.</CommandEmpty>;
}

function ProductCandidateItem(props: {
  candidate: ProductCandidate;
  isSelected: boolean;
  onSelect: () => void;
}): JSX.Element {
  return (
    <CommandItem value={props.candidate.display} onSelect={props.onSelect}>
      <Check className={cn('mr-2 h-4 w-4', props.isSelected ? 'opacity-100' : 'opacity-0')} />
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <span className="truncate">{props.candidate.display}</span>
        <span className="shrink-0 text-xs text-muted-foreground">
          {Math.round(props.candidate.confidence * 100)}%
        </span>
      </div>
    </CommandItem>
  );
}

function ProductCatalogContent(props: {
  debouncedSearchValue: string;
  options: readonly ProductOption[];
  selectedItemNumber: string | undefined;
  onSelect: (itemNumber: string) => void;
  lineNumber: number;
}): JSX.Element {
  if (props.debouncedSearchValue.length < 2) {
    return (
      <div className="px-2 py-3 text-sm text-muted-foreground">
        Type at least 2 characters to search for more products.
      </div>
    );
  }
  return (
    <>
      {props.options.map((option) => (
        <ProductCatalogItem
          key={`catalog-${props.lineNumber}-${option.id}`}
          option={option}
          isSelected={props.selectedItemNumber === option.itemNumber}
          onSelect={props.onSelect}
        />
      ))}
    </>
  );
}

function ProductCatalogItem(props: {
  option: ProductOption;
  isSelected: boolean;
  onSelect: (itemNumber: string) => void;
}): JSX.Element {
  const isObsolete = props.option.isObsolete === true;
  return (
    <CommandItem
      value={props.option.display}
      disabled={isObsolete}
      onSelect={() => {
        if (isObsolete) return;
        props.onSelect(props.option.itemNumber);
      }}
    >
      <Check className={cn('mr-2 h-4 w-4', props.isSelected ? 'opacity-100' : 'opacity-0')} />
      <div className="flex min-w-0 flex-1 items-center justify-between gap-3">
        <span className="truncate">{props.option.display}</span>
        {isObsolete ? (
          <Badge variant="destructive" className="shrink-0">
            Obsolete
          </Badge>
        ) : null}
      </div>
    </CommandItem>
  );
}

function buildCatalogOptions(
  options: readonly ProductOption[],
  topCandidates: readonly ProductCandidate[],
): readonly ProductOption[] {
  const topCandidateNumbers = new Set(topCandidates.map((candidate) => candidate.itemNumber));
  return options.filter((option) => !topCandidateNumbers.has(option.itemNumber));
}

function resolveSelectedProductDisplay(
  line: {
    lineNumber: number;
    matchedProductDisplay?: string | null;
    productCandidates?: readonly ProductCandidate[];
  },
  catalogOptions: readonly ProductOption[],
  selectedProducts: Record<number, string>,
): string {
  const selectedItemNumber = selectedProducts[line.lineNumber];
  if (!selectedItemNumber) return line.matchedProductDisplay ?? '';
  const candidate = line.productCandidates?.find((row) => row.itemNumber === selectedItemNumber);
  if (candidate) return candidate.display;
  const option = catalogOptions.find((row) => row.itemNumber === selectedItemNumber);
  return option?.display ?? line.matchedProductDisplay ?? selectedItemNumber;
}
